import React, { memo, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getCookie, useEffectOnce, usePathParam, useViewport } from '@chilecompra/react-kit'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { Breadcrumb, Breadcrumbs, Skeleton, Spinner, Toast, Typography } from '@chilecompra/react-kit/components'
import AttachFiles from '../../components/Custom/AttachFiles'
import AttachedFiles from '../../components/Custom/AttachedFiles'
import AttachedFilesBlue from '../../components/Custom/AttachedFilesBlue'
import { QUOTE_STATUSES_DICTIONARY, PARTICIPANT_LIST_PREFIX } from '../../config/settings/constants'
import QuoteBasicInformationAlert from './QuoteBasicInformation.alert'
import QuoteBasicInformationDialog from './QuoteBasicInformation.dialog'
import FinesPenalties from './QuoteBasicInformation.finesPenalties'
import Cancellation from './QuoteBasicInformation.cancellation'
import SellerInformation from './QuoteBasicInformation.sellerInformation'
import Errors from '../../components/Custom/Errors'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { topPage } from '../../modules/utils/topPage'
import routes from '../../config/settings/routes'
import { features } from '../../config/settings/features'
import {
  onChangeStatusPurchaseOrderThunk,
  onCleanErrorsPurchaseOrderStatus,
  onDisablePurchaseOrderAlertsThunk
} from '../PurchaseOrderStatus/PurchaseOrderStatus.actions'
import {
  onCleanErrorsQuoteBasicInformation,
  onGetQuoteSummaryThunk,
  onToggleDialogAlert
} from './QuoteBasicInformation.actions'
import {
  onCloseQuoteExtendClosingDateDialog,
  onOpenQuoteExtendClosingDateDialog
} from '../ExtendQuoteClosingDateDialog/ExtendQuoteClosingDateDialog.actions'
import {
  onDeleteFileThunk,
  onGetAttachedFilesThunk,
  onGetDownloadFileThunk,
  onUploadFileThunk
} from '../QuoteStepTwo/QuoteStepTwo.actions'
import { onOpenQuoteRecordsDialog } from '../QuoteRecordsDialog/QuoteRecordsDialog.actions'
import { useHasReachedMaxDate } from '../../modules/hooks/maxDate'
import {
  Action,
  Divider,
  GridContainer,
  GridItem,
  Link,
  Root,
  Tag,
  Text,
  LinkParticipant
} from './QuoteBasicInformation.styles'
import { useProviderFileUrl, useTribunalTcpUrl } from '../../modules/hooks/dynamicUrlGenerator'
import { onDialogCloseSuccess } from '../QuoteBid/QuoteBid.actions'
import { REACT_APP_PARTICIPANT_LIST_URL } from '../../config/settings/environment'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import ProtectByProfile from '../../components/Security/ProtectByProfile'
import { getCookieFromDomain } from '../../modules/cookies'
import { inIframe } from '../../modules/utils/inIframe'
import QuoteNotification from '../QuoteNotification/QuoteNotification.container'
import SustainablePurchases from './QuoteBasicInformation.sustainablePurchases'

const TAG_COLORS_DICTIONARY = {
  [QUOTE_STATUSES_DICTIONARY.CANCELLED]: 'error',
  [QUOTE_STATUSES_DICTIONARY.DESERTED]: 'orange',
  [QUOTE_STATUSES_DICTIONARY.CLOSED]: 'default',
  [QUOTE_STATUSES_DICTIONARY.PUBLISHED]: 'success',
  [QUOTE_STATUSES_DICTIONARY['Proveedor seleccionado']]: 'primary'
}
/**
 * The QuoteBasicInformation's container.
 */
const QuoteBasicInformation = memo(({ children }) => {
  const dispatch = useDispatch()
  const { isSeller, isBuyer } = useAuthorization()
  const quoteId = usePathParam('quoteId')
  const userInfo = Object.fromEntries(new URLSearchParams(getCookieFromDomain('user_info')))
  const navigate = useNavigate()
  const hasReachedMaxDate = useHasReachedMaxDate()
  const {
    basicInfo,
    error,
    errors: errorsQuoteBasicInformation,
    institution,
    loading,
    openDialog,
    purcharseOrderStatus,
    statusCallAgile
  } = useSelector(state => state.quoteBasicInformation)
  const { authInfo } = useSelector(state => state.authorizationProvider)
  const { errors: errorsPurchaseOrderStatus } = useSelector(state => state.purchaseOrderStatus)
  const { attachedFiles, errorAF, loadingAF } = useSelector(state => state.quoteStepTwo)
  const { changeDateClose } = useSelector(state => state.extendQuoteClosingDateDialog)
  const { supplierHability, supplierPrioritized } = useSelector(state => state.quoteSearchHeadingFilters)
  const {
    authInfo: { orgTaxID }
  } = useSelector(state => state.authorizationProvider)

  const { size } = useViewport()
  const isInIframe = useMemo(() => inIframe(), [])
  const handleOpenExtendClosingDateDialog = useCallback(
    () => dispatch(onOpenQuoteExtendClosingDateDialog({ code: quoteId })),
    [basicInfo]
  )

  const isAttachmentRestricted = features['attachments.restricted.to.saved.state']

  const causes = () => {
    if (basicInfo.causes > 0) {
      return `Esta Compra Ágil ha recibido ${basicInfo.causes} demandas ante el`
    } else {
      return `Esta Compra Ágil no ha recibido demandas ante el `
    }
  }
  const useGenerateUrl = () => {
    return useTribunalTcpUrl(quoteId)
  }

  const handleDialogAlert = () => {
    dispatch(onDisablePurchaseOrderAlertsThunk({ quoteId }))

    if (purcharseOrderStatus?.allowChange) {
      dispatch(onToggleDialogAlert())
    }
  }
  const handleSubmitStatusPurchaseOrder = () => {
    dispatch(
      onChangeStatusPurchaseOrderThunk({
        quoteId,
        callback: () => dispatch(onGetQuoteSummaryThunk({ quoteId }))
      })
    )
    dispatch(onToggleDialogAlert())
  }
  const handleUploadFile = file =>
    dispatch(
      onUploadFileThunk({
        quoteCode: quoteId,
        file
      })
    )
  const handleDeleteFile = fileId =>
    dispatch(onDeleteFileThunk({ fileId, quoteCode: quoteId, ipUsuario: userInfo.host || '' }))
  const handleDownloadFile = ({ id, name }) => dispatch(onGetDownloadFileThunk({ id, name }))
  const handleCleanErrors = () => {
    batch(() => {
      dispatch(onCleanErrorsQuoteBasicInformation())
      dispatch(onCleanErrorsPurchaseOrderStatus())
    })
  }
  const handleOpenRecordsDialog = () => dispatch(onOpenQuoteRecordsDialog())

  const handleBackSearch = useCallback(() => {
    const query = getCookie('search')
    navigate({ pathname: routes.search, search: query.toString() }, { replace: true })
  }, [])

  const providerFileUrl = useProviderFileUrl(orgTaxID)

  useEffectOnce(() => {
    dispatch(onDialogCloseSuccess())
    dispatch(onCloseQuoteExtendClosingDateDialog())

    if (!loading) {
      dispatch(onGetQuoteSummaryThunk({ quoteId }))
    }
  })

  useEffect(() => {
    if (changeDateClose === 'OK') {
      dispatch(onGetQuoteSummaryThunk({ quoteId }))
    }
  }, [changeDateClose])

  useEffect(() => {
    dispatch(onGetAttachedFilesThunk({ quoteCode: quoteId }))
  }, [quoteId])

  useEffect(() => {
    topPage(isInIframe, openDialog)
  }, [openDialog])

  return (
    <Root>
      {/* Breadcrumbs */}
      <GridContainer spacing={3}>
        <GridItem xs={12}>
          <Breadcrumbs onClick={handleBackSearch}>
            <Breadcrumb>Escritorio</Breadcrumb>
            <Breadcrumb>Compra ágil</Breadcrumb>
          </Breadcrumbs>
        </GridItem>
      </GridContainer>
      {/* End Breadcrumbs */}

      <GridContainer padding="0 0 6px" spacing={3}>
        <GridItem container alignItems="center" xs={12}>
          {!loading && (
            <>
              <QuoteNotification />
              <Errors errors={supplierHability?.error} providerFileUrl={providerFileUrl} />
              <Errors errors={!statusCallAgile?.isSecondCall && supplierPrioritized?.errors} severity="primary" />
            </>
          )}
          {/* Title */}
          <Typography
            color="black1"
            display="inline"
            fontSize="27px"
            fontWeight="bold"
            lineHeight="31px"
            margin="0"
            variant="h2"
          >
            Detalle de la cotización {quoteId}
          </Typography>
          {loading && <Skeleton margin="0 0 0 25px" width="100px" />}
          {!loading && (
            <Tag
              color={TAG_COLORS_DICTIONARY[basicInfo?.statusId]}
              margin={!size.isDownToTiny && '0 0 0 24px'}
              text={basicInfo?.state}
            />
          )}
          {/* End Title */}
        </GridItem>
      </GridContainer>

      <GridContainer padding="0 0 20px" spacing={3}>
        <GridItem xs={12}>
          <Typography color="black1" fontSize="16px" lineHeight="20px" variant="body1">
            {isSeller &&
              basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED &&
              'Revisa todo el detalle y cotiza, una vez finalizado el proceso te notificaremos si resultas ser el seleccionado.'}
            {!isSeller &&
              basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.CLOSED &&
              'Selecciona al proveedor y emite la orden de compra directamente.'}
            {!isSeller &&
              basicInfo?.statusId !== QUOTE_STATUSES_DICTIONARY.CLOSED &&
              'Una vez llegada la fecha de cierre, podrás revisar el detalle de las cotizaciones recibidas.'}
          </Typography>
        </GridItem>
      </GridContainer>

      <GridContainer margin="0 0 20px" padding="0 21px 0 0">
        <GridItem xs={12}>
          {purcharseOrderStatus?.allowChange && (
            <QuoteBasicInformationAlert purcharseOrderStatus={purcharseOrderStatus} onDialogAlert={handleDialogAlert} />
          )}
        </GridItem>
      </GridContainer>

      <Errors errors={errorsQuoteBasicInformation} onCleanErrors={handleCleanErrors} />
      <Errors errors={errorsPurchaseOrderStatus} onCleanErrors={handleCleanErrors} />

      {/* Main container */}
      <GridContainer spacing={3}>
        {!loading && error && (
          <GridItem xs={12}>
            <Toast
              severity="error"
              title="Lo sentimos, estamos presentando problemas con nuestro servidor."
              textDirection="vertical"
            >
              En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
              minutos más.
            </Toast>
          </GridItem>
        )}
        {basicInfo?.reasonDeserted && (
          <GridItem xs={12}>
            <Toast
              disableIcon
              style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word' }}
              severity="default"
              title="Motivo para declarar desierta esta Compra Ágil."
              textDirection="vertical"
            >
              {basicInfo?.reasonDeserted}
            </Toast>
          </GridItem>
        )}

        <GridItem md={8} xs={12}>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                Nombre
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo?.name || 'Sin información'}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                padding={!size.isDownToTiny && '0 10px 0 0'}
                variant="body2"
              >
                Descripción
              </Typography>
            </GridItem>
            <GridItem xs={12} sm>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo?.description || 'Sin información'}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem xs={12} sm={3}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                Dirección de entrega
              </Typography>
            </GridItem>
            <GridItem xs={12} sm>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo?.address || 'Sin información'}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem xs={12} sm={3}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                {isBuyer ? 'Plazo máximo de entrega' : 'Plazo de entrega'}
              </Typography>
            </GridItem>
            <GridItem xs={12} sm>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo?.deliveryTime || 'Sin información'}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem xs={12} sm={3}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                Presupuesto estimado
              </Typography>
            </GridItem>
            <GridItem xs={12} sm>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? (
                  <Skeleton />
                ) : (
                  formatTypeCurrency(basicInfo?.currency, basicInfo?.amount?.replace(/,/g, ''), true) ||
                  'Sin información'
                )}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem xs={12} sm={3}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                Fecha de publicación
              </Typography>
            </GridItem>
            <GridItem xs={12} sm>
              <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo?.datePublic || 'Sin información'}
              </Typography>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                padding={!size.isDownToTiny && '0 8px 0 0'}
                variant="body2"
              >
                Fecha de cierre primer llamado
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <Text color="black1" fontSize="14px" lineHeight="18px" loading={loading} variant="body2">
                {loading ? <Skeleton /> : statusCallAgile?.dateCloseFirstCall || 'Sin información'}
              </Text>
              {!loading &&
                isBuyer &&
                basicInfo?.dateClose &&
                basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED && (
                  <Link
                    visibility={authInfo.hasObserverRole || hasReachedMaxDate ? 'hidden' : 'visible'}
                    onClick={handleOpenExtendClosingDateDialog}
                  >
                    Ampliar fecha de cierre
                  </Link>
                )}
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                padding={!size.isDownToTiny && '0 8px 0 0'}
                variant="body2"
              >
                Fecha de cierre segundo llamado
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <Text color="black1" fontSize="14px" lineHeight="18px" loading={loading} variant="body2">
                {loading ? <Skeleton /> : statusCallAgile?.secondCallAutomatic || <Skeleton />}
              </Text>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                padding={!size.isDownToTiny && '0 8px 0 0'}
                variant="body2"
              >
                Tipo de presupuesto
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <Text color="black1" fontSize="14px" lineHeight="18px" loading={loading} variant="body2">
                {loading ? <Skeleton /> : basicInfo?.budgetType || <Skeleton />}
              </Text>
            </GridItem>
          </GridContainer>
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography
                color="black1"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="18px"
                padding={size.isDownToTiny ? 0 : '0 8px 0 0'}
                variant="body2"
              >
                Historial
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <Action onClick={handleOpenRecordsDialog}>Historial</Action>
            </GridItem>
          </GridContainer>
          {!loading && basicInfo?.notifiedSellers > 0 && (
            <>
              <Divider />
              <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
                <GridItem xs={12} sm={3}>
                  <Typography
                    color="black1"
                    fontSize="14px"
                    fontWeight="bold"
                    lineHeight="18px"
                    padding={size.isDownToTiny ? 0 : '0 8px 0 0'}
                    variant="body2"
                  >
                    Cantidad de proveedores invitados
                  </Typography>
                </GridItem>
                <GridItem xs={12} sm>
                  <Typography color="black1" fontSize="14px" lineHeight="18px" variant="body2">
                    {`${basicInfo?.notifiedSellers} Proveedores invitados`}
                  </Typography>
                </GridItem>
              </GridContainer>
            </>
          )}
          <Divider />
          <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
            <GridItem sm={3} xs={12}>
              <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                Adjuntos
              </Typography>
            </GridItem>
            <GridItem sm xs={12}>
              <GridContainer>
                {loadingAF && (
                  <GridContainer justifyContent="center">
                    <Spinner />
                  </GridContainer>
                )}
                {!loadingAF && attachedFiles && attachedFiles.length > 0 ? (
                  attachedFiles.map((files, i) =>
                    isBuyer && basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED ? (
                      <AttachedFiles
                        divider={attachedFiles.length - 1 !== i}
                        id={files.id}
                        key={files.id}
                        margin={i === 0 ? '0 0 8px' : '8px 0'}
                        name={files.name}
                        onDeleteFile={isAttachmentRestricted ? null : handleDeleteFile}
                        onDownloadFile={handleDownloadFile}
                      />
                    ) : (
                      <AttachedFilesBlue
                        id={files.id}
                        key={files.id}
                        name={files.name}
                        onDownloadFile={handleDownloadFile}
                      />
                    )
                  )
                ) : (
                  <Typography color="black3" fontSize="14px" lineHeight="18px" variant="body2">
                    No existen documentos adjuntos
                  </Typography>
                )}
                {isBuyer && !isAttachmentRestricted && basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.PUBLISHED && (
                  <AttachFiles
                    error={errorAF}
                    loading={loadingAF}
                    margin="64px 0 0"
                    paddingSubtitle="0 16px 0 0"
                    onUploadFile={handleUploadFile}
                  />
                )}
              </GridContainer>
            </GridItem>
          </GridContainer>
          <ProtectByProfile rule={userType => userType === 'Comprador'} fallback={() => null}>
            <ProtectByFeatures rule={features => features['link.participants']}>
              <Divider />
              <GridContainer padding={size.isDownToTiny ? '10px 0 10px 10px' : '14px 0 14px 24px'}>
                <GridItem sm={3} xs={12}>
                  <Typography color="black1" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                    Listado de participantes
                  </Typography>
                </GridItem>
                <GridItem sm xs={12}>
                  <LinkParticipant
                    textDecoration="underline"
                    target="_blank"
                    href={`${REACT_APP_PARTICIPANT_LIST_URL}/participantes/${PARTICIPANT_LIST_PREFIX}/${quoteId}`}
                  >
                    Ir al listado de participantes
                  </LinkParticipant>
                </GridItem>
              </GridContainer>
            </ProtectByFeatures>
          </ProtectByProfile>
        </GridItem>
        <GridItem md xs={12}>
          <SellerInformation
            company={institution.company}
            dni={institution.dni}
            loading={loading}
            organization={institution.organization}
            causes={causes()}
            tcpUrl={useGenerateUrl()}
          />
          <SustainablePurchases
            isEnvironmental={basicInfo?.isEnvironmental}
            isSocialEconomic={basicInfo?.isSocialEconomic}
          />
          {basicInfo?.statusId === QUOTE_STATUSES_DICTIONARY.CANCELLED && (
            <Cancellation
              reasonCancellation={basicInfo?.reasonCancellation}
              cancellationType={basicInfo?.cancellationType}
            />
          )}
          {basicInfo?.finesPenalties && <FinesPenalties />}
        </GridItem>
        <GridItem md={8} xs={12}>
          {children}
        </GridItem>
      </GridContainer>
      {/* End Main container */}

      <QuoteBasicInformationDialog
        handleDialogAlert={handleDialogAlert}
        handleSubmitStatusPurchaseOrder={handleSubmitStatusPurchaseOrder}
        openDialog={openDialog}
        quoteId={quoteId}
      />
    </Root>
  )
})

QuoteBasicInformation.propTypes = {
  children: PropTypes.node.isRequired
}

export default QuoteBasicInformation
